import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ADMIN_BULK_USERS_MAX,
  passwordRules,
  type AdminUpdateUserInput,
  type AdminUser,
  type UserSession,
} from '@orbit-sync/shared-types';
import { Avatar } from '../../components/Avatar.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { EmptyState } from '../../components/EmptyState.js';
import { Field } from '../../components/Field.js';
import { FormBanner } from '../../components/FormBanner.js';
import { Icon } from '../../components/Icon.js';
import { api, errorMessage, fieldErrorsFrom, type FieldErrors } from '../../lib/api/index.js';
import { useSettingsStore } from '../../settings/settingsStore.js';
import { describeAgent } from '../../lib/userAgent.js';
import { useAdminAuthStore } from '../adminAuthStore.js';
import { formatDate, useDebounced } from '../adminHelpers.js';
import { PAGE_SIZE, Pager, SearchBox } from '../adminUi.js';
import { useAdminQuery } from '../useAdminQuery.js';

/** Every account: search, edit name / email / password, delete. */
export function AdminUsersPage() {
  // Operators may only enable and disable accounts; the server enforces it too.
  const full = useAdminAuthStore((state) => state.role === 'full');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [sessionsOf, setSessionsOf] = useState<AdminUser | null>(null);
  const [disabling, setDisabling] = useState<AdminUser | null>(null);
  // Bulk selection, kept across pages and cleared when the search changes.
  const [selected, setSelected] = useState<ReadonlySet<string>>(EMPTY_SELECTION);
  const [bulkDisabling, setBulkDisabling] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const q = useDebounced(query.trim());
  const { data, error, loading, reload } = useAdminQuery(
    (signal) => api.admin.listUsers({ q, limit: PAGE_SIZE, offset }, signal),
    `${q}|${offset}`,
  );

  async function confirmDelete(): Promise<void> {
    if (!deleting) {
      return;
    }

    setBusy(true);
    setActionError(null);

    try {
      await api.admin.deleteUser(deleting.id);
      setDeleting(null);
      reload();
    } catch (cause) {
      setActionError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const pageIds = (data?.items ?? []).map((user) => user.id);
  const pageSelected = pageIds.filter((id) => selected.has(id)).length;

  function toggle(ids: string[], on: boolean): void {
    setSelected((current) => {
      const next = new Set(current);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  }

  /** Every account matching the search, not just this page. */
  async function selectAllMatching(): Promise<void> {
    setSelectingAll(true);
    setActionError(null);

    try {
      const ids: string[] = [];
      let total = Infinity;

      while (ids.length < total) {
        const page = await api.admin.listUsers({ q, limit: 100, offset: ids.length });
        total = page.total;
        ids.push(...page.items.map((user) => user.id));

        if (page.items.length === 0) {
          break;
        }
      }

      setSelected(new Set(ids));
    } catch (cause) {
      setActionError(errorMessage(cause));
    } finally {
      setSelectingAll(false);
    }
  }

  /** Applies to the whole selection, in batches the server accepts. */
  async function setSelectedDisabled(disabled: boolean): Promise<void> {
    const ids = [...selected];
    setNotice(null);

    await runAction(async () => {
      let updated = 0;

      for (let start = 0; start < ids.length; start += ADMIN_BULK_USERS_MAX) {
        const batch = ids.slice(start, start + ADMIN_BULK_USERS_MAX);
        updated += (await api.admin.setUsersDisabled(batch, disabled)).updated;
      }

      setSelected(EMPTY_SELECTION);
      setBulkDisabling(false);
      setNotice(
        `${disabled ? 'Disabled' : 'Enabled'} ${updated} account${updated === 1 ? '' : 's'}.`,
      );
    });
  }

  async function runAction(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setActionError(null);

    try {
      await action();
      reload();
    } catch (cause) {
      setActionError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-page">
      <header className="admin-head">
        <div>
          <h1>Users</h1>
          <p className="muted small">Every account in the workspace. Search by name or email.</p>
        </div>
      </header>

      <div className="admin-toolbar">
        <SearchBox
          value={query}
          placeholder="Search users"
          onChange={(value) => {
            setQuery(value);
            setOffset(0);
            setSelected(EMPTY_SELECTION);
          }}
        />
        {loading ? <span className="btn-spinner muted" aria-label="Loading" /> : null}
      </div>

      <FormBanner message={error} />
      {!deleting && !disabling && !bulkDisabling ? <FormBanner message={actionError} /> : null}
      {notice ? (
        <p className="field-hint" role="status">
          <Icon name="check" size={13} /> {notice}
        </p>
      ) : null}

      {selected.size > 0 ? (
        <div className="admin-bulk-bar" role="toolbar" aria-label="Selected users">
          <strong>{selected.size} selected</strong>
          {data && selected.size < data.total ? (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={selectingAll || busy}
              onClick={() => void selectAllMatching()}
            >
              {selectingAll ? <span className="btn-spinner" /> : null}
              Select all {data.total}
              {q ? ' matching' : ''}
            </button>
          ) : null}
          <span className="admin-bulk-actions">
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={busy}
              onClick={() => void setSelectedDisabled(false)}
            >
              <Icon name="check" size={14} />
              Enable
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              disabled={busy}
              onClick={() => {
                setActionError(null);
                setBulkDisabling(true);
              }}
            >
              <Icon name="userX" size={14} />
              Disable
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={busy}
              onClick={() => setSelected(EMPTY_SELECTION)}
            >
              Clear
            </button>
          </span>
        </div>
      ) : null}

      {data && data.items.length === 0 ? (
        <EmptyState compact icon="users" title="No users match" />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="cell-check">
                  <SelectBox
                    label="Select every user on this page"
                    checked={pageIds.length > 0 && pageSelected === pageIds.length}
                    indeterminate={pageSelected > 0 && pageSelected < pageIds.length}
                    onChange={(on) => toggle(pageIds, on)}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th className="num">Channels</th>
                <th className="num">Messages</th>
                <th>Last message</th>
                <th>Joined</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((user) => (
                <tr key={user.id} className={selected.has(user.id) ? 'row--selected' : undefined}>
                  <td className="cell-check">
                    <SelectBox
                      label={`Select ${user.displayName}`}
                      checked={selected.has(user.id)}
                      onChange={(on) => toggle([user.id], on)}
                    />
                  </td>
                  <td>
                    <span className="cell-person">
                      <Avatar
                        name={user.displayName}
                        id={user.id}
                        size="sm"
                        presence={user.online ? 'online' : 'offline'}
                      />
                      <span className="truncate" dir="auto">
                        {user.displayName}
                      </span>
                    </span>
                  </td>
                  <td className="truncate">{user.email}</td>
                  <td>
                    <span className="badge-row">
                      {user.authSource === 'ldap' ? (
                        <span className="tag tag--directory" title="Signs in through the directory">
                          Directory
                        </span>
                      ) : null}
                      {user.disabledAt ? (
                        <span
                          className="tag tag--disabled"
                          title={
                            user.disabledBy === 'directory'
                              ? 'No longer in the directory'
                              : 'Disabled by an administrator'
                          }
                        >
                          Disabled
                        </span>
                      ) : null}
                      {user.locked ? (
                        <span className="tag tag--locked" title="Too many failed sign-ins">
                          Locked
                        </span>
                      ) : null}
                      {user.authSource !== 'ldap' && !user.disabledAt && !user.locked ? (
                        <span className="muted small">Active</span>
                      ) : null}
                    </span>
                  </td>
                  <td className="num">{user.channelCount}</td>
                  <td className="num">{user.messageCount}</td>
                  <td>{formatDate(user.lastMessageAt, true)}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td className="cell-actions">
                    {!full || user.authSource === 'ldap' ? null : (
                      <button
                        type="button"
                        className="btn btn--icon btn--sm"
                        aria-label={`Edit ${user.displayName}`}
                        title="Edit"
                        onClick={() => setEditing(user)}
                      >
                        <Icon name="pencil" size={14} />
                      </button>
                    )}
                    {full ? (
                      <button
                        type="button"
                        className="btn btn--icon btn--sm"
                        aria-label={`Sessions of ${user.displayName}`}
                        title="Signed-in sessions"
                        onClick={() => setSessionsOf(user)}
                      >
                        <Icon name="key" size={14} />
                      </button>
                    ) : null}
                    {full && user.locked ? (
                      <button
                        type="button"
                        className="btn btn--icon btn--sm"
                        aria-label={`Unlock ${user.displayName}`}
                        title="Unlock sign-in"
                        disabled={busy}
                        onClick={() => void runAction(() => api.admin.unlockUser(user.id))}
                      >
                        <Icon name="unlock" size={14} />
                      </button>
                    ) : null}
                    {user.disabledAt ? (
                      <button
                        type="button"
                        className="btn btn--icon btn--sm"
                        aria-label={`Enable ${user.displayName}`}
                        title="Enable account"
                        disabled={busy}
                        onClick={() =>
                          void runAction(() => api.admin.setUserDisabled(user.id, false))
                        }
                      >
                        <Icon name="check" size={14} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--icon btn--sm btn--icon-danger"
                        aria-label={`Disable ${user.displayName}`}
                        title="Disable account"
                        onClick={() => {
                          setActionError(null);
                          setDisabling(user);
                        }}
                      >
                        <Icon name="userX" size={14} />
                      </button>
                    )}
                    {full ? (
                      <button
                        type="button"
                        className="btn btn--icon btn--sm btn--icon-danger"
                        aria-label={`Delete ${user.displayName}`}
                        title="Delete"
                        onClick={() => {
                          setActionError(null);
                          setDeleting(user);
                        }}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data ? <Pager offset={offset} total={data.total} onChange={setOffset} /> : null}

      {editing ? (
        <UserEditDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}

      {sessionsOf ? <SessionsDialog user={sessionsOf} onClose={() => setSessionsOf(null)} /> : null}

      {disabling ? (
        <ConfirmDialog
          title={`Disable ${disabling.displayName}?`}
          confirmLabel="Disable account"
          busy={busy}
          error={actionError}
          onConfirm={() =>
            void runAction(async () => {
              await api.admin.setUserDisabled(disabling.id, true);
              setDisabling(null);
            })
          }
          onCancel={() => setDisabling(null)}
        >
          <p>
            <strong>{disabling.email}</strong> is signed out everywhere now and cannot sign in until
            you enable the account again. Their messages stay; they disappear from the people
            picker.
          </p>
        </ConfirmDialog>
      ) : null}

      {bulkDisabling ? (
        <ConfirmDialog
          title={`Disable ${selected.size} account${selected.size === 1 ? '' : 's'}?`}
          confirmLabel="Disable accounts"
          busy={busy}
          error={actionError}
          onConfirm={() => void setSelectedDisabled(true)}
          onCancel={() => setBulkDisabling(false)}
        >
          <p>
            They are signed out everywhere now and cannot sign in until enabled again - one by one,
            or with Enable on a new selection. Their messages stay.
          </p>
        </ConfirmDialog>
      ) : null}

      {deleting ? (
        <ConfirmDialog
          title={`Delete ${deleting.displayName}?`}
          confirmLabel="Delete user"
          requireText="DELETE"
          busy={busy}
          error={actionError}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setDeleting(null)}
        >
          <p>
            This permanently deletes <strong>{deleting.email}</strong>, all of their messages, their
            direct conversations, and any group they would leave with a single member. They are
            signed out immediately.
          </p>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

function UserEditDialog({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const settings = useSettingsStore((state) => state.settings);
  const ref = useRef<HTMLDialogElement>(null);
  const [displayName, setDisplayName] = useState(user.displayName);
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const rules = password ? passwordRules(password, settings) : [];

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    const changes: AdminUpdateUserInput = {};

    if (displayName.trim() !== user.displayName) {
      changes.displayName = displayName.trim();
    }

    if (email.trim().toLowerCase() !== user.email) {
      changes.email = email.trim();
    }

    if (password) {
      changes.password = password;
    }

    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    setBusy(true);
    setFormError(null);
    setFieldErrors({});

    try {
      await api.admin.updateUser(user.id, changes);
      onSaved();
    } catch (cause) {
      setFieldErrors(fieldErrorsFrom(cause));
      setFormError(errorMessage(cause));
      setBusy(false);
    }
  }

  return (
    <dialog ref={ref} className="modal" onClose={onClose} aria-labelledby="edit-user-title">
      <form className="modal-form" onSubmit={(event) => void submit(event)} noValidate>
        <header className="modal-head">
          <div>
            <h2 id="edit-user-title">Edit user</h2>
            <p>Changes apply immediately. Leave the password empty to keep it.</p>
          </div>
          <button type="button" className="btn btn--icon" onClick={onClose} aria-label="Close">
            <Icon name="x" size={17} />
          </button>
        </header>

        <div className="modal-scroll">
          <FormBanner message={formError} />
          <Field
            label="Display name"
            name="edit-display-name"
            dir="auto"
            maxLength={settings.maxDisplayNameLength}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            error={fieldErrors.displayName}
          />
          <Field
            label="Email"
            name="edit-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={fieldErrors.email}
          />
          <Field
            label="New password"
            name="edit-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={fieldErrors.password}
          />
          {rules.length > 0 ? (
            <ul className="password-rules">
              {rules.map((rule) => (
                <li
                  key={rule.id}
                  className={rule.met ? 'password-rule password-rule--met' : 'password-rule'}
                >
                  <Icon name={rule.met ? 'check' : 'x'} size={12} />
                  {rule.label}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <footer className="modal-foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? <span className="btn-spinner" /> : <Icon name="check" size={14} />}
            Save changes
          </button>
        </footer>
      </form>
    </dialog>
  );
}

/** Where someone is signed in, with a way to end each session or all of them. */
function SessionsDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [sessions, setSessions] = useState<UserSession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    api.admin
      .listUserSessions(user.id, controller.signal)
      .then(setSessions)
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) {
          setError(errorMessage(cause));
        }
      });

    return () => controller.abort();
  }, [user.id, nonce]);

  async function end(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      await action();
      setNonce((value) => value + 1);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog ref={ref} className="modal" onClose={onClose} aria-labelledby="sessions-title">
      <header className="modal-head">
        <div>
          <h2 id="sessions-title">Sessions</h2>
          <p className="truncate">Where {user.displayName} is signed in</p>
        </div>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label="Close">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="modal-scroll">
        <FormBanner message={error} />
        {sessions === null && !error ? <p className="muted small">Loading…</p> : null}
        {sessions?.length === 0 ? (
          <EmptyState compact icon="lock" title="Not signed in anywhere" />
        ) : null}
        <ul className="session-list">
          {sessions?.map((session) => (
            <li key={session.id} className="session-row">
              <span className="session-body">
                <strong className="truncate">{describeAgent(session.userAgent)}</strong>
                <span className="field-hint">
                  {session.method === 'ldap' ? 'Directory' : 'Password'} ·{' '}
                  {session.ip ?? 'unknown address'}
                  {' · '}signed in {formatDate(session.createdAt, true)}
                  {' · '}last active {formatDate(session.lastUsedAt, true)}
                  {' · '}ends by {formatDate(session.expiresAt, true)}
                </span>
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={busy}
                onClick={() => void end(() => api.admin.endSession(session.id))}
              >
                <Icon name="logOut" size={13} />
                End
              </button>
            </li>
          ))}
        </ul>
      </div>

      <footer className="modal-foot">
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          Close
        </button>
        <button
          type="button"
          className="btn btn--danger"
          disabled={busy || !sessions?.length}
          onClick={() => void end(() => api.admin.endUserSessions(user.id))}
        >
          <Icon name="logOut" size={14} />
          Sign out everywhere
        </button>
      </footer>
    </dialog>
  );
}

const EMPTY_SELECTION: ReadonlySet<string> = new Set();

/** A row checkbox; `indeterminate` exists only as a DOM property, hence the ref. */
function SelectBox({
  label,
  checked,
  indeterminate = false,
  onChange,
}: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}
